/**
 * 🔄 useHistory Hook - Undo/Redo System
 * 
 * Verwaltet History Stack für Widget-Änderungen
 * - Undo/Redo Funktionalität
 * - Keyboard Shortcuts (Ctrl+Z / Ctrl+Y)
 * - History Limit (max 50 States)
 * - Debounced State Tracking
 */

import { useState, useCallback, useEffect, useRef } from 'react';

const MAX_HISTORY_SIZE = 50; // Maximale History-Größe

export const useHistory = (initialState) => {
    // History Stack: Array of states
    const [history, setHistory] = useState([initialState]);
    
    // Current position in history (0 = oldest, length-1 = newest)
    const [currentIndex, setCurrentIndex] = useState(0);
    
    // Track if we're in the middle of an undo/redo operation
    const isUndoRedoRef = useRef(false);
    
    // Debounce timer for grouping rapid changes
    const debounceTimerRef = useRef(null);
    
    // Get current state
    const currentState = history[currentIndex];
    
    // Can undo?
    const canUndo = currentIndex > 0;
    
    // Can redo?
    const canRedo = currentIndex < history.length - 1;
    
    /**
     * Push new state to history
     * - Removes all "future" states if we're in the middle
     * - Limits history size
     * - Debounces rapid changes
     */
    const pushState = useCallback((newState, options = {}) => {
        const { immediate = false } = options;
        
        // Skip if we're in undo/redo operation
        if (isUndoRedoRef.current) {
            return;
        }
        
        // Clear debounce timer if immediate
        if (immediate && debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        
        // Debounce function
        const doPush = () => {
            setHistory(prev => {
                // Remove all states after current index (clear "future")
                const newHistory = prev.slice(0, currentIndex + 1);
                
                // Add new state
                newHistory.push(newState);
                
                // Limit history size (keep most recent)
                if (newHistory.length > MAX_HISTORY_SIZE) {
                    newHistory.shift(); // Remove oldest
                    setCurrentIndex(newHistory.length - 1);
                } else {
                    setCurrentIndex(newHistory.length - 1);
                }
                
                console.log('📚 [HISTORY] State pushed. Total states:', newHistory.length);
                
                return newHistory;
            });
        };
        
        // Immediate push or debounced
        if (immediate) {
            doPush();
        } else {
            // Clear existing timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            
            // Debounce for 1 second (group rapid changes)
            debounceTimerRef.current = setTimeout(() => {
                doPush();
                debounceTimerRef.current = null;
            }, 1000);
        }
    }, [currentIndex]);
    
    /**
     * Undo - Go back one step
     */
    const undo = useCallback(() => {
        if (!canUndo) {
            console.log('⚠️ [HISTORY] Cannot undo - at oldest state');
            return;
        }
        
        isUndoRedoRef.current = true;
        
        setCurrentIndex(prev => {
            const newIndex = prev - 1;
            console.log('⏪ [HISTORY] Undo - Moving to state', newIndex);
            return newIndex;
        });
        
        // Reset flag after state update
        setTimeout(() => {
            isUndoRedoRef.current = false;
        }, 100);
    }, [canUndo]);
    
    /**
     * Redo - Go forward one step
     */
    const redo = useCallback(() => {
        if (!canRedo) {
            console.log('⚠️ [HISTORY] Cannot redo - at newest state');
            return;
        }
        
        isUndoRedoRef.current = true;
        
        setCurrentIndex(prev => {
            const newIndex = prev + 1;
            console.log('⏩ [HISTORY] Redo - Moving to state', newIndex);
            return newIndex;
        });
        
        // Reset flag after state update
        setTimeout(() => {
            isUndoRedoRef.current = false;
        }, 100);
    }, [canRedo]);
    
    /**
     * Clear all history and reset to current state
     */
    const clearHistory = useCallback((newInitialState) => {
        const stateToUse = newInitialState || currentState;
        setHistory([stateToUse]);
        setCurrentIndex(0);
        console.log('🗑️ [HISTORY] History cleared');
    }, [currentState]);
    
    /**
     * Keyboard Shortcuts - Ctrl+Z (Undo) / Ctrl+Y (Redo)
     */
    useEffect(() => {
        const handleKeyboard = (e) => {
            // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
            const isCtrlOrCmd = e.ctrlKey || e.metaKey;
            
            if (!isCtrlOrCmd) return;
            
            // Ctrl+Z - Undo
            if (e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                if (canUndo) {
                    console.log('⌨️ [HISTORY] Keyboard: Undo (Ctrl+Z)');
                    undo();
                }
            }
            
            // Ctrl+Y or Ctrl+Shift+Z - Redo
            if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) {
                e.preventDefault();
                if (canRedo) {
                    console.log('⌨️ [HISTORY] Keyboard: Redo (Ctrl+Y)');
                    redo();
                }
            }
        };
        
        window.addEventListener('keydown', handleKeyboard);
        
        return () => {
            window.removeEventListener('keydown', handleKeyboard);
        };
    }, [canUndo, canRedo, undo, redo]);
    
    /**
     * Cleanup debounce timer on unmount
     */
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);
    
    return {
        // Current state
        state: currentState,
        
        // Actions
        pushState,
        undo,
        redo,
        clearHistory,
        
        // State info
        canUndo,
        canRedo,
        historySize: history.length,
        currentIndex,
        
        // Debug info
        history: history.map((_, i) => ({ index: i, isCurrent: i === currentIndex }))
    };
};

export default useHistory;

